/**
 * Запрет выделения текста во время работы на доске.
 *
 * На iOS (палец, а особенно Apple Pencil) Safari в произвольный момент
 * начинал выделять текст — и синим подсвечивалась вся страница: доска,
 * панель инструментов, шапка. Это обычное выделение документа, которое
 * браузер начинает сам, когда касание «поехало» по тексту.
 *
 * CSS `user-select: none` один не спасает: выделение может начаться с
 * элемента, которому выделение разрешено (поле ввода, вывод консоли),
 * и дальше разъехаться на весь документ. Поэтому на время касания доски
 * гасим само событие начала выделения.
 */

/** Где выделение нужно оставить: поля ввода, редакторы кода, вывод. */
const SELECTABLE = [
  "input",
  "textarea",
  "select",
  "[contenteditable='true']",
  ".cm-editor",
  ".wb-text-overlay-editor",
  ".wb-code-card__stdout",
  ".wb-sql-card__out",
  ".wb-board-help",
  // Панель свойств работает с выделенным куском текста: нажатие на её кнопки
  // не должно снимать выделение (иначе «сделать жирным» применялось бы в пустоту).
  ".wb-selection-toolbar",
].join(",");

/** Выделение внутри поля ввода — это работа человека с текстом, не мусор. */
function selectionIsInEditable(sel: Selection | null): boolean {
  if (!sel || sel.isCollapsed) return false;
  const node = sel.anchorNode;
  const el = node instanceof Element ? node : node?.parentElement ?? null;
  return Boolean(el?.closest(SELECTABLE));
}

function isSelectableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest(SELECTABLE));
}

let installs = 0;
let gestureActive = false;

function onPointerDown(ev: PointerEvent): void {
  if (isSelectableTarget(ev.target)) {
    gestureActive = false;
    return;
  }
  gestureActive = true;
  // Уже начатое выделение (например, от прошлого касания) снимаем сразу,
  // иначе синяя подсветка останется висеть на всей странице. Выделенный
  // кусок текста в поле ввода не трогаем — с ним сейчас работают.
  const sel = window.getSelection?.();
  if (sel && !sel.isCollapsed && !selectionIsInEditable(sel)) sel.removeAllRanges();
}

function endGesture(): void {
  gestureActive = false;
}

function onSelectStart(ev: Event): void {
  if (!gestureActive) return;
  if (isSelectableTarget(ev.target)) return;
  ev.preventDefault();
}

/**
 * Включает защиту на всё время жизни доски. Возвращает функцию отключения;
 * несколько досок на странице считаются по счётчику.
 */
export function installBoardSelectionGuard(): () => void {
  installs += 1;
  if (installs === 1) {
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointerup", endGesture, true);
    document.addEventListener("pointercancel", endGesture, true);
    document.addEventListener("selectstart", onSelectStart, true);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    installs -= 1;
    if (installs === 0) {
      gestureActive = false;
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointerup", endGesture, true);
      document.removeEventListener("pointercancel", endGesture, true);
      document.removeEventListener("selectstart", onSelectStart, true);
    }
  };
}
